/* ==========================================================================
   sdx-ugc-videos.js — 5-kart UGC slider
   - Sadece ortadaki video oynar (muted/loop/playsinline).
   - Slide edildiğinde yeni center play, eski center pause + seek(0).
   - IntersectionObserver: section görünür değilse hiç oynama.
   - preload="none": yanındaki kartlar; center "metadata"+"auto" upgrade.
   - prefers-reduced-motion: autoplay kapalı, manuel play.
   - Mobile (<=720px): snap-scroll; %60+ görünür kart oynar.
   ========================================================================== */

(function () {
  'use strict';

  var MOBILE_BREAKPOINT = 720;
  var SECTION_VISIBILITY_THRESHOLD = 0.6;
  var MOBILE_VISIBILITY_THRESHOLD = 0.6;

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isMobile() {
    return window.innerWidth <= MOBILE_BREAKPOINT;
  }

  /* ---------------- Video source hydration ---------------- */
  function hydrateSources(video, preloadLevel) {
    if (!video) return;

    if (!video.dataset.hydrated) {
      // Multi-source <source data-src> pattern
      var sources = video.querySelectorAll('source[data-src]');
      if (sources.length) {
        sources.forEach(function (s) {
          if (!s.getAttribute('src')) s.setAttribute('src', s.dataset.src);
        });
        video.load();
      } else if (video.dataset.src && !video.getAttribute('src')) {
        // Single src via data-src (video_url fallback)
        video.setAttribute('src', video.dataset.src);
        video.load();
      }
      video.dataset.hydrated = '1';
    }

    if (preloadLevel) {
      video.preload = preloadLevel;
    }
  }

  /* ---------------- Single slider instance ---------------- */
  function UgcSlider(root) {
    this.root = root;
    this.track = root.querySelector('[data-sdx-ugc-track]');
    this.cards = Array.prototype.slice.call(root.querySelectorAll('[data-sdx-ugc-card]'));
    this.prevBtn = root.querySelector('[data-sdx-ugc-prev]');
    this.nextBtn = root.querySelector('[data-sdx-ugc-next]');
    this.dots = Array.prototype.slice.call(root.querySelectorAll('[data-sdx-ugc-dot]'));

    this.autoplayCenter = root.dataset.autoplayCenter === '1';
    this.total = this.cards.length;
    this.centerIndex = parseInt(root.dataset.initialCenter, 10) || 0;
    this.isVisible = false;
    this.mobile = isMobile();
    this.reducedMotion = prefersReducedMotion();

    if (!this.track || this.total === 0) return;

    this._bind();
    this._updateCenter(this.centerIndex, { skipPlay: true });
    this._observeSection();
    this._observeResize();

    if (this.mobile) {
      this._observeMobileCards();
    }
  }

  UgcSlider.prototype._bind = function () {
    var self = this;

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function () { self.shift(-1); });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function () { self.shift(1); });
    }

    this.dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        var idx = parseInt(dot.dataset.index, 10);
        if (!isNaN(idx)) self._updateCenter(idx);
      });
    });

    // Click on non-center card → bring to center
    this.cards.forEach(function (card) {
      card.addEventListener('click', function (e) {
        if (card.classList.contains('sdx-ugc__card--center')) return;
        // don't intercept clicks on product link
        if (e.target.closest('[data-sdx-ugc-product]')) return;
        var idx = parseInt(card.dataset.index, 10);
        if (!isNaN(idx)) self._updateCenter(idx);
      });
    });

    // Manual play button (reduced motion or error fallback)
    this.cards.forEach(function (card) {
      var playBtn = card.querySelector('[data-sdx-ugc-play]');
      if (!playBtn) return;
      playBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        var idx = parseInt(card.dataset.index, 10);
        if (isNaN(idx)) return;
        if (idx !== self.centerIndex) {
          self._updateCenter(idx);
        } else {
          self._playCard(card);
        }
      });
    });

    // Keyboard nav (section focused)
    this.root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); self.shift(1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); self.shift(-1); }
    });

    // Mute button — toggle audio on the card's video.
    // Single-audio policy: when unmuting one card, force-mute all others.
    this.cards.forEach(function (card) {
      var btn = card.querySelector('[data-sdx-ugc-mute]');
      if (!btn) return;
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var video = card.querySelector('[data-sdx-ugc-video]');
        if (!video) return;
        var pressed = btn.getAttribute('aria-pressed') === 'true';
        var willUnmute = !pressed;
        btn.setAttribute('aria-pressed', willUnmute ? 'true' : 'false');
        btn.setAttribute('aria-label', willUnmute ? 'Sesi kapat' : 'Sesi aç');
        if (willUnmute) {
          // Mute all others first to prevent overlapping audio
          self.cards.forEach(function (c) {
            if (c === card) return;
            var v = c.querySelector('[data-sdx-ugc-video]');
            if (v) v.muted = true;
            var b = c.querySelector('[data-sdx-ugc-mute]');
            if (b) {
              b.setAttribute('aria-pressed', 'false');
              b.setAttribute('aria-label', 'Sesi aç');
            }
          });
        }
        video.muted = !willUnmute;
        // Best-effort play() so unmute also resumes if paused.
        if (willUnmute && video.paused) {
          var p; try { p = video.play(); } catch (_) {}
          if (p && p.catch) p.catch(function () {});
        }
      });
    });
  };

  UgcSlider.prototype.shift = function (direction) {
    if (this.total === 0) return;
    var next = (this.centerIndex + direction + this.total) % this.total;
    this._updateCenter(next);
  };

  UgcSlider.prototype._updateCenter = function (newIndex, opts) {
    opts = opts || {};
    if (newIndex < 0 || newIndex >= this.total) return;

    var oldIndex = this.centerIndex;
    this.centerIndex = newIndex;

    // Update classes
    this.cards.forEach(function (card, i) {
      card.classList.toggle('sdx-ugc__card--center', i === newIndex);
    });
    this.dots.forEach(function (dot, i) {
      dot.classList.toggle('is-active', i === newIndex);
    });

    // Track transform via CSS var (desktop only; mobile uses native scroll)
    if (!this.mobile) {
      var centerOffset = this._computeCenterOffset(newIndex);
      this.track.style.setProperty('--sdx-ugc-center-index', String(centerOffset));
    }

    // Pause old center, reset
    if (oldIndex !== newIndex) {
      this._pauseCard(this.cards[oldIndex], true);
    }

    // Preload neighbors @ metadata, center @ auto
    this._hydrateNeighborhood(newIndex);

    // Play new center (if allowed)
    if (!opts.skipPlay) {
      this._playCenterIfAllowed();
    }
  };

  /**
   * Compute how many "slots" the track needs to shift so the centerIndex card
   * sits in the middle of the viewport.
   */
  UgcSlider.prototype._computeCenterOffset = function (idx) {
    // The flex container centers its children (justify-content: center on stage).
    // Relative to that natural center, shift by (idx - middleIndex).
    var middle = (this.total - 1) / 2;
    return idx - middle;
  };

  UgcSlider.prototype._hydrateNeighborhood = function (idx) {
    var self = this;
    this.cards.forEach(function (card, i) {
      var video = card.querySelector('[data-sdx-ugc-video]');
      if (!video) return;
      var distance = Math.min(
        Math.abs(i - idx),
        self.total - Math.abs(i - idx) // wrap distance
      );
      // All non-center cards keep preload='metadata' so the first frame renders
      // as a poster fallback. Setting 'none' here strips the buffered frame,
      // producing the gray/black tile bug on far cards.
      if (i === idx) {
        hydrateSources(video, 'auto');
      } else {
        hydrateSources(video, 'metadata');
      }
    });
  };

  UgcSlider.prototype._playCard = function (card) {
    if (!card) return;
    var video = card.querySelector('[data-sdx-ugc-video]');
    if (!video) return;

    hydrateSources(video, 'auto');

    // "Intent to play" — distinct from "actually playing":
    // is-playing  = JS attempted play()      → hide play button only
    // is-ready    = video has buffered+playing → fade in video / fade out poster
    card.classList.add('is-playing');

    var readyMark = function () {
      if (video.dataset.released === '1') return;
      video.classList.add('is-ready');
      card.classList.add('is-ready');
    };

    // Use playing event (preferred — fires when actual playback starts)
    // Fallback to canplay for browsers that delay playing event.
    video.addEventListener('playing', readyMark, { once: true });
    video.addEventListener('canplay', function () {
      // Don't mark ready on canplay alone — wait for actual play, but
      // if play() already resolved we should be safe to show video.
      if (!video.paused) readyMark();
    }, { once: true });

    // Wait until video is at least metadata-loaded before calling play(),
    // otherwise load() ↔ play() race produces "AbortError: The play() request
    // was interrupted by a new load request".
    var tryPlay = function () {
      var attempt;
      try { attempt = video.play(); } catch (_) { return; }
      if (!attempt || typeof attempt.catch !== 'function') return;
      attempt.catch(function (err) {
        if (err && err.name === 'AbortError') {
          // Load got interrupted — schedule one retry after a tick.
          setTimeout(function () {
            if (card.classList.contains('is-playing') && video.dataset.released !== '1') {
              try { video.play(); } catch (_) {}
            }
          }, 250);
          return;
        }
        // Autoplay blocked or other error — surface play button again.
        card.classList.remove('is-playing');
      });
    };

    if (video.readyState >= 2 /* HAVE_CURRENT_DATA */) {
      tryPlay();
    } else {
      video.addEventListener('loadeddata', tryPlay, { once: true });
      // Safety net: if loadeddata never fires (rare), still attempt after delay.
      setTimeout(function () {
        if (video.dataset.released !== '1' && card.classList.contains('is-playing') && video.paused) {
          tryPlay();
        }
      }, 1500);
    }

    video.addEventListener('error', function () {
      card.classList.remove('is-playing');
      card.classList.remove('is-ready');
      video.classList.remove('is-ready');
    }, { once: true });
  };

  UgcSlider.prototype._pauseCard = function (card, reset) {
    if (!card) return;
    var video = card.querySelector('[data-sdx-ugc-video]');
    if (!video) return;
    // Mark released so any pending play retry bails out.
    video.dataset.released = '1';
    try { video.pause(); } catch (_) { /* noop */ }
    if (reset) {
      try { video.currentTime = 0; } catch (_) { /* noop */ }
    }
    card.classList.remove('is-playing');
    card.classList.remove('is-ready');
    video.classList.remove('is-ready');
    // Clear release flag on next tick so future _playCard works.
    setTimeout(function () { try { delete video.dataset.released; } catch (_) {} }, 0);
  };

  UgcSlider.prototype._playCenterIfAllowed = function () {
    if (!this.autoplayCenter) return;
    if (this.reducedMotion) return;
    if (!this.isVisible) return;
    if (this.mobile) return; // mobile uses IO-per-card
    var card = this.cards[this.centerIndex];
    this._playCard(card);
  };

  UgcSlider.prototype._pauseAll = function () {
    var self = this;
    this.cards.forEach(function (card) { self._pauseCard(card, false); });
  };

  /* ---------------- Section visibility IO (desktop) ---------------- */
  UgcSlider.prototype._observeSection = function () {
    if (!('IntersectionObserver' in window)) {
      this.isVisible = true;
      this._playCenterIfAllowed();
      return;
    }
    var self = this;
    this._sectionIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        self.isVisible = entry.isIntersecting && entry.intersectionRatio >= SECTION_VISIBILITY_THRESHOLD;
        if (self.isVisible) {
          self._playCenterIfAllowed();
        } else {
          self._pauseAll();
        }
      });
    }, { threshold: [0, SECTION_VISIBILITY_THRESHOLD, 1] });
    this._sectionIO.observe(this.root);
  };

  /* ---------------- Per-card IO (mobile snap-scroll) ---------------- */
  UgcSlider.prototype._observeMobileCards = function () {
    if (!('IntersectionObserver' in window)) return;
    var self = this;
    this._cardIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var card = entry.target;
        if (entry.isIntersecting && entry.intersectionRatio >= MOBILE_VISIBILITY_THRESHOLD) {
          if (!self.reducedMotion && self.autoplayCenter) {
            self._playCard(card);
          }
        } else {
          self._pauseCard(card, false);
        }
      });
    }, { threshold: [0, MOBILE_VISIBILITY_THRESHOLD, 1] });
    this.cards.forEach(function (c) { self._cardIO.observe(c); });
  };

  /* ---------------- Resize breakpoint swap ---------------- */
  UgcSlider.prototype._observeResize = function () {
    var self = this;
    var lastMobile = this.mobile;
    var onResize = function () {
      var nowMobile = isMobile();
      if (nowMobile === lastMobile) return;
      lastMobile = nowMobile;
      self.mobile = nowMobile;
      self._pauseAll();

      if (nowMobile) {
        // reset inline transform var to default
        self.track.style.removeProperty('--sdx-ugc-center-index');
        self._observeMobileCards();
      } else {
        if (self._cardIO) { self._cardIO.disconnect(); self._cardIO = null; }
        self._updateCenter(self.centerIndex);
      }
    };
    window.addEventListener('resize', onResize, { passive: true });
    this._resizeHandler = onResize;
  };

  UgcSlider.prototype.destroy = function () {
    if (this._sectionIO) this._sectionIO.disconnect();
    if (this._cardIO) this._cardIO.disconnect();
    if (this._resizeHandler) window.removeEventListener('resize', this._resizeHandler);
    this._pauseAll();
  };

  /* ---------------- Mount ---------------- */
  var instances = new WeakMap();

  function mount(root) {
    if (!root || instances.has(root)) return;
    var inst = new UgcSlider(root);
    instances.set(root, inst);
  }

  function unmount(root) {
    var inst = instances.get(root);
    if (inst) { inst.destroy(); instances.delete(root); }
  }

  function init() {
    document.querySelectorAll('[data-sdx-ugc]').forEach(mount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify theme editor support
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-sdx-ugc]');
    if (root) mount(root);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector('[data-sdx-ugc]');
    if (root) unmount(root);
  });
  document.addEventListener('shopify:section:select', function (e) {
    var root = e.target.querySelector('[data-sdx-ugc]');
    if (!root) return;
    var inst = instances.get(root);
    if (inst) inst._playCenterIfAllowed();
  });
})();
